import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    type ChartData,
    type ChartOptions,
    Legend,
    LinearScale,
    type ScriptableContext,
    Title,
    Tooltip,
} from 'chart.js';

import {
    adaptPatternToDailyDistribution,
    adaptPatternToHourlyDistribution,
    type TemporalDistributionData,
} from '@/entities/pattern';

import { alternatingBackgroundPlugin } from '@/shared/constants/charts';
import { DEFAULT_PATTERN_LAB_COLORS } from '@/shared/constants/color';
import { cleanupCustomTooltip, createCustomTooltip } from '@/shared/lib/chart';
import { orderWeekDays } from '@/shared/lib/datetime';
import { useTheme, useUserTimezone } from '@/shared/lib/hooks';
import { logError } from '@/shared/lib/logging';
import { Card } from '@/shared/ui/card';
import { createDataLabelsPlugin } from '@/shared/ui/chart-data-labels-plugin';
import { TimezoneSelector } from '@/shared/ui/timezone-selector';
import { TitleWithHelp } from '@/shared/ui/title-with-help';
import { GradientBadge } from '@/shared/ui/value-formatter';

import { usePatternLabMode } from '../../model/selectors/pattern-lab-mode';
import type { PatternStudy } from '../../model/types/pattern-study';
import { PatternLabTitle } from '../pattern-lab-title/pattern-lab-title';

import cls from './pattern-lab-distribution.module.css';

// Helper function to translate day names
const translateDayName = (dayName: string): string => {
    const dayMap: Record<string, string> = {
        Monday: 'days.monday',
        Tuesday: 'days.tuesday',
        Wednesday: 'days.wednesday',
        Thursday: 'days.thursday',
        Friday: 'days.friday',
        Saturday: 'days.saturday',
        Sunday: 'days.sunday',
    };

    return dayMap[dayName] || dayName;
};

// Local helper replacing removed '../../lib/pattern-lab-median-adapter'
const getMixedPatternColor = (patterns: PatternStudy[]): string => {
    const hexToRgb = (
        hex: string,
    ): { r: number; g: number; b: number } | null => {
        const m = hex.trim().match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);

        if (!m) return null;

        return {
            r: parseInt(m[1]!, 16),
            g: parseInt(m[2]!, 16),
            b: parseInt(m[3]!, 16),
        };
    };

    const colors = patterns
        .map((p) => p.color)
        .filter((c): c is string => typeof c === 'string' && c.length > 0);

    if (colors.length === 0) {
        return DEFAULT_PATTERN_LAB_COLORS[0]!;
    }

    let rSum = 0;
    let gSum = 0;
    let bSum = 0;
    let count = 0;

    colors.forEach((hex) => {
        const rgb = hexToRgb(hex);

        if (rgb) {
            rSum += rgb.r;
            gSum += rgb.g;
            bSum += rgb.b;
            count += 1;
        }
    });

    if (count === 0) {
        return DEFAULT_PATTERN_LAB_COLORS[0]!;
    }

    const toHex = (n: number): string => n.toString(16).padStart(2, '0');
    const r = Math.round(rSum / count);
    const g = Math.round(gSum / count);
    const b = Math.round(bSum / count);

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
);

const BORDER_RADIUS = 4;
const GRID_COLOR = 'rgba(129, 139, 166, 0.2)';

const getPatternColor = (
    patternColor: string | undefined,
    index: number,
): string =>
    patternColor ||
    DEFAULT_PATTERN_LAB_COLORS[index % DEFAULT_PATTERN_LAB_COLORS.length]!;

const TRANSLATION_NAMESPACE = 'pattern-studio';
const BULLISH_PERCENTAGE_KEY = 'patternStats.bullishPercentage';
const BEARISH_PERCENTAGE_KEY = 'patternStats.bearishPercentage';

const Chart = memo(
    ({
        data,
        patterns,
        minHeight = 400,
        visiblePatterns = undefined,
        mixedColor = undefined,
    }: {
        data: TemporalDistributionData[][];
        patterns: PatternStudy[];
        minHeight?: number;
        visiblePatterns?: Record<string, boolean>;
        mixedColor?: string | undefined;
    }) => {
        const chartRef = useRef<ChartJS<'bar'>>(null);
        const { t } = useTranslation(TRANSLATION_NAMESPACE);
        const { t: tCommon } = useTranslation();
        const isDarkTheme = useTheme()[2];

        useEffect(
            () => () => {
                cleanupCustomTooltip();
            },
            [],
        );

        const TEXT_COLOR = isDarkTheme
            ? 'rgba(255, 255, 255, 0.7)'
            : 'rgba(0, 0, 0, 0.7)';

        // Collect all unique time intervals across all patterns
        const allTimeIntervals = new Set<string>();

        data.forEach((dataSet) => {
            dataSet.forEach((item) => {
                allTimeIntervals.add(item.timeInterval);
            });
        });

        // Convert to array and sort
        // Check if the intervals are weekdays and use orderWeekDays if they are
        const timeIntervalsArray = Array.from(allTimeIntervals);
        const isContainingWeekdays = timeIntervalsArray.some((interval) =>
            [
                'Monday',
                'Tuesday',
                'Wednesday',
                'Thursday',
                'Friday',
                'Saturday',
                'Sunday',
            ].includes(interval),
        );

        const timeIntervals = isContainingWeekdays
            ? orderWeekDays(timeIntervalsArray)
            : timeIntervalsArray.sort();

        // Create combined labels with sentiment for X-axis
        const combinedLabels: string[] = [];

        timeIntervals.forEach((interval) => {
            const translationKey = translateDayName(interval);
            const translatedInterval = tCommon(translationKey);

            combinedLabels.push(`${translatedInterval} Bearish`);
            combinedLabels.push(`${translatedInterval} Bullish`);
        });

        const createPatternGradient = (
            context: ScriptableContext<'bar'>,
            patternColor: string,
        ) => {
            const { ctx, chartArea } = context.chart;

            if (!chartArea) {
                return patternColor;
            }

            const gradient = ctx.createLinearGradient(
                0,
                chartArea.bottom,
                0,
                chartArea.top,
            );

            const r = parseInt(patternColor.slice(1, 3), 16);
            const g = parseInt(patternColor.slice(3, 5), 16);
            const b = parseInt(patternColor.slice(5, 7), 16);
            const darkerColor = `rgb(${Math.floor(r * 0.5)}, ${Math.floor(g * 0.5)}, ${Math.floor(b * 0.5)})`;

            gradient.addColorStop(0, patternColor);
            gradient.addColorStop(1, darkerColor);

            return gradient;
        };

        const datasets: Array<{
            label: string;
            data: number[];
            backgroundColor: (
                context: ScriptableContext<'bar'>,
            ) => string | CanvasGradient;
            borderColor: string;
            borderWidth: number;
            borderRadius: {
                topLeft: number;
                topRight: number;
                bottomLeft: number;
                bottomRight: number;
            };
            categoryPercentage: number;
            barPercentage: number;
            grouped: boolean;
        }> = [];

        // Reorganize data by timeInterval first, then by bullish/bearish type
        // This creates a structure where for each timeInterval, we have bullish and bearish groups

        // First, collect all pattern data with their colors and names
        const patternData: Array<{
            pattern: PatternStudy;
            bullishData: number[];
            bearishData: number[];
            color: string;
            name: string;
            id: string;
        }> = [];

        data.forEach((dataSet, i) => {
            const pattern = patterns[i];

            if (!pattern) return;

            const patternId = pattern.pattern.unique_id;

            // Skip this pattern if it's explicitly set to not visible
            if (visiblePatterns && visiblePatterns[patternId] === false) {
                return;
            }

            const patternName = pattern.name ?? patternId;
            // Use mixedColor if provided (for aggregation mode), otherwise use pattern color
            const patternColor =
                mixedColor || getPatternColor(pattern.color, i);

            // Map the data to the complete set of time intervals
            const bullishData = timeIntervals.map((interval) => {
                const item = dataSet.find((d) => d.timeInterval === interval);

                return item ? item.bullishCount : 0; // Use 0 for missing values
            });

            const bearishData = timeIntervals.map((interval) => {
                const item = dataSet.find((d) => d.timeInterval === interval);

                return item ? item.bearishCount : 0; // Use 0 for missing values
            });

            patternData.push({
                pattern,
                bullishData,
                bearishData,
                color: patternColor,
                name: patternName,
                id: patternId,
            });
        });

        // Create datasets for each pattern with data aligned to combined labels
        if (timeIntervals.length > 0 && patternData.length > 0) {
            // For each pattern, create a dataset
            patternData.forEach((pattern) => {
                // Create a dataset for this pattern
                const dataArray: number[] = [];

                // For each time interval, add bearish and bullish data in order
                timeIntervals.forEach(
                    (_interval: string, intervalIndex: number) => {
                        // Add bearish data for this interval
                        dataArray.push(pattern.bearishData[intervalIndex] ?? 0);
                        // Add bullish data for this interval
                        dataArray.push(pattern.bullishData[intervalIndex] ?? 0);
                    },
                );

                datasets.push({
                    label: pattern.name,
                    data: dataArray,
                    backgroundColor: (context: ScriptableContext<'bar'>) =>
                        createPatternGradient(context, pattern.color),
                    borderColor: pattern.color,
                    borderWidth: 0,
                    borderRadius: {
                        topLeft: BORDER_RADIUS,
                        topRight: BORDER_RADIUS,
                        bottomLeft: 0,
                        bottomRight: 0,
                    },
                    categoryPercentage: 0.8,
                    barPercentage: 0.6,
                    grouped: true,
                });
            });
        }

        const chartData: ChartData<'bar'> = {
            labels: combinedLabels,
            datasets,
        };

        const chartOptions: ChartOptions<'bar'> = {
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: 'x',
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: false,
                    external: createCustomTooltip({
                        isDark: isDarkTheme,
                        formatItems: (tooltipModel) =>
                            tooltipModel.dataPoints.map((point) => {
                                const datasetLabel = point.dataset.label || '';
                                const labelWithSentiment =
                                    (point.chart.data.labels?.[
                                        point.dataIndex
                                    ] as string) || '';
                                const sentiment = labelWithSentiment.includes(
                                    'Bearish',
                                )
                                    ? t('patternStats.bearish')
                                    : t('patternStats.bullish');

                                return {
                                    color: point.dataset.borderColor as string,
                                    label: `${datasetLabel} - ${sentiment}`,
                                    value: point.parsed.y,
                                };
                            }),
                    }),
                },
            },
            scales: {
                x: {
                    grid: {
                        display: true,
                        color: GRID_COLOR,
                        drawTicks: false,
                        lineWidth: (context) =>
                            context.index % 2 === 0 ? 1 : 0,
                    },
                    ticks: {
                        display: true,
                        color: TEXT_COLOR,
                        font: {
                            size: 10,
                        },
                        padding: 10,
                        maxRotation: 45,
                        minRotation: 45,
                    },
                },
                y: {
                    grid: {
                        display: true,
                        color: GRID_COLOR,
                        drawTicks: false,
                        lineWidth: 1,
                    },
                    ticks: {
                        color: TEXT_COLOR,
                        callback: (value) => {
                            const numValue = Number(value);

                            return Number.isInteger(numValue)
                                ? `${numValue}`
                                : '';
                        },
                        display: true,
                        padding: 10,
                        stepSize: 1,
                        precision: 0,
                    },
                    suggestedMin: 0,
                    suggestedMax: (() => {
                        let maxValue = 0;

                        datasets.forEach((dataset) => {
                            if (dataset.data) {
                                const datasetMax = Math.max(
                                    ...dataset.data.map(Number),
                                );

                                maxValue = Math.max(maxValue, datasetMax);
                            }
                        });

                        return maxValue > 0 ? maxValue + 1 : undefined;
                    })(),
                },
            },
        };

        return (
            <div
                className={cls.chart}
                style={{ minHeight: `${minHeight}px` }}
            >
                <Bar
                    ref={chartRef}
                    data={chartData}
                    options={chartOptions}
                    plugins={[
                        alternatingBackgroundPlugin,
                        createDataLabelsPlugin({
                            isDarkTheme,
                            displayType: 'percentage',
                            fontSize: '10px',
                        }),
                    ]}
                />
            </div>
        );
    },
);

Chart.displayName = 'Chart';

const DataTable = memo(({ data }: { data: TemporalDistributionData[] }) => {
    const { t } = useTranslation(TRANSLATION_NAMESPACE);
    const { t: tCommon } = useTranslation();

    return (
        <table className={cls.table}>
            <thead>
                <tr>
                    <th>{t('patternStats.timeInterval')}</th>
                    <th>{t('patternStats.counter')}</th>
                    <th>{t(BULLISH_PERCENTAGE_KEY)}</th>
                    <th>{t(BEARISH_PERCENTAGE_KEY)}</th>
                    <th>{t('patternStats.notVerifiedPercentage')}</th>
                </tr>
            </thead>
            <tbody>
                {data.map((row) => {
                    const translationKey = translateDayName(row.timeInterval);
                    const translatedInterval = tCommon(translationKey);

                    return (
                        <tr key={row.timeInterval}>
                            <td>{translatedInterval}</td>
                            <td>{row.counter}</td>
                            <td aria-label={t(BULLISH_PERCENTAGE_KEY)}>
                                <GradientBadge
                                    label={`${row.bullishPercentage}%`}
                                    variant="green"
                                />
                            </td>
                            <td aria-label={t(BEARISH_PERCENTAGE_KEY)}>
                                <GradientBadge
                                    label={`${row.bearishPercentage}%`}
                                    variant="red"
                                />
                            </td>
                            <td
                                aria-label={t(
                                    'patternStats.notVerifiedPercentage',
                                )}
                            >
                                <GradientBadge
                                    label={`${row.notVerifiedPercentage}%`}
                                    variant="blue"
                                />
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
});

DataTable.displayName = 'DataTable';

interface PatternTableSectionProps {
    title: string;
    data: TemporalDistributionData[];
    dataTour?: string;
}

const PatternTableSection = memo(
    ({ title, data, dataTour }: PatternTableSectionProps) => (
        <div
            className={cls.tableContainer}
            data-tour={dataTour}
        >
            <div className={cls.chartHeader}>
                <div className={cls.chartTitle}>{title}</div>
            </div>
            <DataTable data={data} />
        </div>
    ),
);

PatternTableSection.displayName = 'PatternTableSection';

interface CombinedChartSectionProps {
    title: string;
    data: TemporalDistributionData[][];
    patterns: PatternStudy[];
    helpId?: string | undefined;
    isAggregationMode?: boolean;
    aggregatedPatternIds?: string;
    mixedColor?: string;
    dataTour?: string;
}

const CombinedChartSection = memo(
    ({
        title,
        data,
        patterns,
        helpId,
        isAggregationMode = false,
        aggregatedPatternIds,
        mixedColor,
        dataTour,
    }: CombinedChartSectionProps) => {
        const [visiblePatterns, setVisiblePatterns] = useState<
            Record<string, boolean>
        >(() => {
            // Initialize all patterns as visible
            const initialState: Record<string, boolean> = {};

            patterns?.forEach((pattern) => {
                initialState[pattern.pattern.unique_id] = true;
            });

            return initialState;
        });

        // Reset all checkboxes to true when patterns change
        useEffect(() => {
            const initialState: Record<string, boolean> = {};

            patterns?.forEach((pattern) => {
                initialState[pattern.pattern.unique_id] = true;
            });

            setVisiblePatterns(initialState);
        }, [patterns]);

        const togglePatternVisibility = (patternId: string) => {
            setVisiblePatterns((prev) => ({
                ...prev,
                [patternId]: !prev[patternId],
            }));
        };

        if (!patterns || patterns.length === 0) {
            return null;
        }

        return (
            <div
                className={cls.graphContainer}
                data-tour={dataTour}
            >
                <div className={cls.graphHeader}>
                    <TitleWithHelp
                        helpId={helpId}
                        title={title}
                    />
                </div>

                <div className={cls.patternControls}>
                    {isAggregationMode && aggregatedPatternIds ? (
                        // In aggregation mode, show a single entry with all pattern IDs
                        <label
                            className={cls.patternCheckbox}
                            htmlFor="aggregated-patterns"
                            style={{
                                borderLeft: `3px solid ${
                                    mixedColor ||
                                    patterns?.[0]?.color ||
                                    getPatternColor(patterns?.[0]?.color, 0)
                                }`,
                            }}
                        >
                            <input
                                checked
                                id="aggregated-patterns"
                                readOnly
                                type="checkbox"
                            />
                            <span>{aggregatedPatternIds}</span>
                        </label>
                    ) : (
                        // In comparison mode, show each pattern separately
                        patterns?.map((pattern) => {
                            const patternId = pattern.pattern.unique_id;
                            const patternName = pattern.name ?? patternId;
                            const patternColor =
                                pattern.color ||
                                getPatternColor(
                                    pattern.color,
                                    patterns.indexOf(pattern),
                                );

                            return (
                                <label
                                    key={patternId}
                                    className={cls.patternCheckbox}
                                    htmlFor={patternId}
                                    style={{
                                        borderLeft: `3px solid ${patternColor}`,
                                    }}
                                >
                                    <input
                                        checked={
                                            visiblePatterns[patternId] !== false
                                        }
                                        id={patternId}
                                        onChange={() =>
                                            togglePatternVisibility(patternId)
                                        }
                                        type="checkbox"
                                    />
                                    <span>{patternName}</span>
                                </label>
                            );
                        }) || []
                    )}
                </div>

                <Chart
                    data={data}
                    minHeight={400}
                    mixedColor={isAggregationMode ? mixedColor : undefined}
                    patterns={patterns || []}
                    visiblePatterns={visiblePatterns}
                />
            </div>
        );
    },
);

CombinedChartSection.displayName = 'CombinedChartSection';

type PatternStudyDistributionProps = {
    patterns: PatternStudy[];
};

export const PatternLabDistribution = memo(
    ({ patterns }: PatternStudyDistributionProps) => {
        const { t } = useTranslation(TRANSLATION_NAMESPACE);
        const mode = usePatternLabMode();
        const [userTimezone, setUserTimezone] = useUserTimezone();

        const HOURLY_TITLE = t('patternStats.hourlyDistribution');
        const DAILY_TITLE = t('patternStats.dailyDistribution');
        const ALL_PATTERNS_HOURLY = `${t('patternStats.allPatterns')} - ${HOURLY_TITLE}`;
        const ALL_PATTERNS_DAILY = `${t('patternStats.allPatterns')} - ${DAILY_TITLE}`;

        const hourlyDistributionsForAllPatterns: TemporalDistributionData[][] =
            [];
        const dailyDistributionsForAllPatterns: TemporalDistributionData[][] =
            [];

        // Process all patterns to extract distribution data
        patterns.forEach((patternStudy) => {
            const { pattern } = patternStudy;

            try {
                const hourlyDistributionData = adaptPatternToHourlyDistribution(
                    pattern,
                    userTimezone,
                );

                hourlyDistributionsForAllPatterns.push(hourlyDistributionData);
            } catch (error) {
                logError(
                    `Failed to load hourly distribution data for pattern ${pattern.unique_id}:`,
                    error,
                );
            }

            try {
                const dailyDistributionData =
                    adaptPatternToDailyDistribution(pattern);

                dailyDistributionsForAllPatterns.push(dailyDistributionData);
            } catch (error) {
                logError(
                    `Failed to load daily distribution data for pattern ${pattern.unique_id}:`,
                    error,
                );
            }
        });

        // Function to merge distribution data for aggregation mode
        const mergeDistributionData = (
            distributionsArray: TemporalDistributionData[][],
        ): TemporalDistributionData[] => {
            if (distributionsArray.length === 0) return [];

            // Create a map to store merged data by timeInterval
            const mergedDataMap = new Map<
                string,
                {
                    timeInterval: string;
                    counter: number;
                    bullishPercentage: number;
                    bearishPercentage: number;
                    notVerifiedPercentage: number;
                    bullishCount: number;
                    bearishCount: number;
                    notVerifiedCount: number;
                    totalCount: number;
                }
            >();

            // Process all distributions
            distributionsArray.forEach((distribution) => {
                distribution.forEach((item) => {
                    const { timeInterval } = item;
                    const existingData = mergedDataMap.get(timeInterval);

                    // Use the direct count values from the data
                    const { bullishCount, bearishCount, notVerifiedCount } =
                        item;

                    if (existingData) {
                        // Update existing data
                        existingData.counter += item.counter;
                        existingData.bullishCount += bullishCount;
                        existingData.bearishCount += bearishCount;
                        existingData.notVerifiedCount += notVerifiedCount;
                        existingData.totalCount =
                            existingData.bullishCount +
                            existingData.bearishCount +
                            existingData.notVerifiedCount;
                    } else {
                        // Create new entry
                        mergedDataMap.set(timeInterval, {
                            timeInterval,
                            counter: item.counter,
                            bullishPercentage: 0, // Will be calculated later
                            bearishPercentage: 0, // Will be calculated later
                            notVerifiedPercentage: 0, // Will be calculated later
                            bullishCount,
                            bearishCount,
                            notVerifiedCount,
                            totalCount:
                                bullishCount + bearishCount + notVerifiedCount,
                        });
                    }
                });
            });

            // Convert map to array and calculate percentages
            return Array.from(mergedDataMap.values()).map((item) => {
                // Recalculate percentages based on total counts
                const bullishPercentage =
                    item.totalCount > 0
                        ? Number(
                              (
                                  (item.bullishCount / item.totalCount) *
                                  100
                              ).toFixed(2),
                          )
                        : 0;
                const bearishPercentage =
                    item.totalCount > 0
                        ? Number(
                              (
                                  (item.bearishCount / item.totalCount) *
                                  100
                              ).toFixed(2),
                          )
                        : 0;
                const notVerifiedPercentage =
                    item.totalCount > 0
                        ? Number(
                              (
                                  (item.notVerifiedCount / item.totalCount) *
                                  100
                              ).toFixed(2),
                          )
                        : 0;

                return {
                    timeInterval: item.timeInterval,
                    counter: item.counter,
                    bullishPercentage,
                    bearishPercentage,
                    notVerifiedPercentage,
                    bullishCount: item.bullishCount,
                    bearishCount: item.bearishCount,
                    notVerifiedCount: item.notVerifiedCount,
                };
            });
        };

        // Merge distribution data for aggregation mode
        const mergedHourlyDistribution = mergeDistributionData(
            hourlyDistributionsForAllPatterns,
        );
        const mergedDailyDistribution = mergeDistributionData(
            dailyDistributionsForAllPatterns,
        );

        const renderComparisonMode = () => (
            <div className={cls.container}>
                {patterns.map((patternStudy) => {
                    const { pattern } = patternStudy;
                    const patternName = patternStudy.name ?? pattern.unique_id;

                    let hourlyDistributionData: TemporalDistributionData[] = [];
                    let dailyDistributionData: TemporalDistributionData[] = [];

                    try {
                        hourlyDistributionData =
                            adaptPatternToHourlyDistribution(
                                pattern,
                                userTimezone,
                            );
                    } catch (error) {
                        logError(
                            `Failed to load hourly distribution data for pattern ${pattern.unique_id}:`,
                            error,
                        );
                    }

                    try {
                        dailyDistributionData =
                            adaptPatternToDailyDistribution(pattern);
                    } catch (error) {
                        logError(
                            `Failed to load daily distribution data for pattern ${pattern.unique_id}:`,
                            error,
                        );
                    }

                    return (
                        <div
                            key={pattern.unique_id}
                            className={cls.patternSection}
                        >
                            <Card
                                accentColor={patternStudy.color}
                                hasShadow={false}
                                hasSnowflake={false}
                            >
                                <PatternLabTitle title={patternName} />
                                <div
                                    className={cls.tablesRow}
                                    data-tour="pattern-lab-distribution-tables"
                                >
                                    {hourlyDistributionData.length > 0 && (
                                        <PatternTableSection
                                            data={hourlyDistributionData}
                                            title={HOURLY_TITLE}
                                        />
                                    )}

                                    {dailyDistributionData.length > 0 && (
                                        <PatternTableSection
                                            data={dailyDistributionData}
                                            title={DAILY_TITLE}
                                        />
                                    )}
                                </div>
                            </Card>
                        </div>
                    );
                })}

                <div
                    className={cls.graphsSection}
                    data-tour="pattern-lab-distribution-graphs"
                >
                    {hourlyDistributionsForAllPatterns.length > 0 && (
                        <CombinedChartSection
                            data={hourlyDistributionsForAllPatterns}
                            helpId="LAB_HOURLY_DISTRIBUTION"
                            patterns={patterns}
                            title={ALL_PATTERNS_HOURLY}
                        />
                    )}

                    {dailyDistributionsForAllPatterns.length > 0 ? (
                        <CombinedChartSection
                            data={dailyDistributionsForAllPatterns}
                            helpId="LAB_DAILY_DISTRIBUTION"
                            patterns={patterns}
                            title={ALL_PATTERNS_DAILY}
                        />
                    ) : (
                        <div className={cls.graphContainer}>
                            <div className={cls.graphHeader}>
                                <div className={cls.graphTitle}>
                                    {ALL_PATTERNS_DAILY}
                                </div>
                            </div>
                            <div className={cls.chart}>
                                <p>{t('patternStats.noDataAvailable')}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );

        const renderAggregationMode = () => {
            // Collect pattern IDs for display
            const patternIds =
                patterns.length > 0
                    ? patterns
                          .map(
                              (patternStudy) =>
                                  patternStudy.name ??
                                  patternStudy.pattern.unique_id,
                          )
                          .join(', ')
                    : '';

            // Get mixed color from all patterns
            const mixedColor = getMixedPatternColor(patterns);

            return (
                <div className={cls.container}>
                    <div className={cls.patternSection}>
                        <Card
                            hasShadow={false}
                            hasSnowflake={false}
                        >
                            <PatternLabTitle title={patternIds} />
                            <div
                                className={cls.tablesRow}
                                data-tour="pattern-lab-distribution-tables"
                            >
                                {mergedHourlyDistribution.length > 0 && (
                                    <PatternTableSection
                                        data={mergedHourlyDistribution}
                                        title={HOURLY_TITLE}
                                    />
                                )}

                                {mergedDailyDistribution.length > 0 && (
                                    <PatternTableSection
                                        data={mergedDailyDistribution}
                                        title={DAILY_TITLE}
                                    />
                                )}
                            </div>
                        </Card>
                    </div>

                    <div
                        className={cls.graphsSection}
                        data-tour="pattern-lab-distribution-graphs"
                    >
                        {mergedHourlyDistribution.length > 0 &&
                            patterns.length > 0 && (
                                <CombinedChartSection
                                    aggregatedPatternIds={patternIds}
                                    data={[mergedHourlyDistribution]}
                                    helpId="LAB_HOURLY_DISTRIBUTION"
                                    isAggregationMode
                                    mixedColor={mixedColor}
                                    patterns={
                                        patterns.length > 0 && patterns[0]
                                            ? [patterns[0]]
                                            : []
                                    }
                                    title={HOURLY_TITLE}
                                />
                            )}

                        {mergedDailyDistribution.length > 0 &&
                        patterns.length > 0 ? (
                            <CombinedChartSection
                                aggregatedPatternIds={patternIds}
                                data={[mergedDailyDistribution]}
                                helpId="LAB_DAILY_DISTRIBUTION"
                                isAggregationMode
                                mixedColor={mixedColor}
                                patterns={
                                    patterns.length > 0 && patterns[0]
                                        ? [patterns[0]]
                                        : []
                                }
                                title={DAILY_TITLE}
                            />
                        ) : (
                            <div className={cls.graphContainer}>
                                <div className={cls.graphHeader}>
                                    <div className={cls.graphTitle}>
                                        {DAILY_TITLE}
                                    </div>
                                </div>
                                <div className={cls.chart}>
                                    <p>{t('patternStats.noDataAvailable')}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            );
        };

        // Handle timezone change
        const handleTimezoneChange = useCallback(
            (timezone: string) => {
                setUserTimezone(timezone);
            },
            [setUserTimezone],
        );

        useEffect(() => {
            // Remount the component if the user timezone changes
        }, [userTimezone]);

        if (!patterns || patterns.length === 0) {
            return null;
        }

        // Check if there's any distribution data to display
        const hasDistributionData =
            hourlyDistributionsForAllPatterns.length > 0 ||
            dailyDistributionsForAllPatterns.length > 0 ||
            mergedHourlyDistribution.length > 0 ||
            mergedDailyDistribution.length > 0;

        if (!hasDistributionData) {
            return null;
        }

        return (
            <Card
                data-tour="pattern-lab-distribution"
                headerActions={
                    <TimezoneSelector onChange={handleTimezoneChange} />
                }
                helpId="PATTERN_LAB_DISTRIBUTION"
                isCollapsible
                title={t('patternStats.temporalDistributionPattern')}
            >
                {mode === 'comparison'
                    ? renderComparisonMode()
                    : renderAggregationMode()}
            </Card>
        );
    },
);

PatternLabDistribution.displayName = 'PatternStudyDistribution';
